import { Link, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, Loading, ModuleHero, Notice, StatCard, StatusPill } from '../components/ui';
import { BAND, COMPANY_STATUS, FEATURE, RESOLVER, VERDICT, companyDisplayName, flagEmoji, formatDateTime, formatPct, formatTokens, formatUsd, pctTone } from '../lib/format';
import type { CompanyDetail } from '../types';
import CompanyLogo from '../components/CompanyLogo';

const TH = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#c084a0]';
const TD = 'px-3 py-2.5 align-top';

export default function CompanyAi() {
  const { id } = useParams();
  const { data, loading, error } = useApi<CompanyDetail>(id ? `/support/ai/companies/${id}` : null, 15000);

  if (loading && !data) return <Loading label="Loading company AI history…" />;
  if (error && !data) return <Notice tone="rose">{error}</Notice>;
  if (!data) return null;

  const { company, documents, reports, usage } = data;
  const pipelineUsage = usage.filter((u) => u.feature !== 'DEMO_DOC_GENERATION');
  const cost = pipelineUsage.reduce((a, u) => a + u.costUsd, 0);
  const failed = pipelineUsage.filter((u) => !u.success);
  const comparable = documents.filter((d) => d.agreement !== null);
  const agreed = comparable.filter((d) => d.agreement).length;
  const docPct = comparable.length ? Math.round((agreed / comparable.length) * 1000) / 10 : null;
  const confidences = documents.filter((d) => d.aiVerdict && d.aiConfidence != null).map((d) => d.aiConfidence!);
  const avgConfidence = confidences.length ? Math.round(confidences.reduce((a, b) => a + b, 0) / confidences.length) : null;
  const status = COMPANY_STATUS[company.status];

  return (
    <div className="space-y-6">
      <Link to="/" className="text-xs font-bold text-[#d6006e] hover:underline">
        ← AI monitor
      </Link>
      <ModuleHero
        eyebrow={`${flagEmoji(company.countryCode)} ${company.countryCode} · ${company.companyType || 'type not set'}`}
        title={companyDisplayName(company)}
        description={[company.sector, company.annualRevenue && `Revenue ${company.annualRevenue}`, company.fundingAmount && `Ask ${company.fundingAmount}`].filter(Boolean).join(' · ')}
        actions={
          <div className="flex items-center gap-3">
            <CompanyLogo companyId={company.id} name={companyDisplayName(company)} hasLogo={company.hasLogo} className="h-14 w-14 rounded-xl" />
            <div className="text-right text-xs text-[#b0708f]">
              <StatusPill label={status?.label || company.status} tone={status?.tone || 'slate'} />
              <p className="mt-1">Customer: {company.customer?.email || '—'}</p>
              <p>Analyst: {company.assignedAdmin?.name || company.assignedAdmin?.email || '—'}</p>
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="AI cost (pipeline)" value={formatUsd(cost)} hint={`${pipelineUsage.length} calls · ${formatTokens(pipelineUsage.reduce((a, u) => a + u.totalTokens, 0))} tokens`} />
        <StatCard label="Document accuracy" value={formatPct(docPct)} tone={pctTone(docPct)} hint={`${agreed}/${comparable.length} AI verdicts confirmed by the analyst`} />
        <StatCard label="Avg AI confidence" value={avgConfidence != null ? `${avgConfidence}%` : '—'} hint="Across documents the AI could read" />
        <StatCard label="Failed AI calls" value={failed.length} tone={failed.length ? 'rose' : 'green'} hint={failed.length ? 'See the call log below' : 'No failures'} />
      </div>

      <Card title="Ratings — AI draft vs published">
        {reports.length === 0 && <p className="text-sm text-[#c084a0]">No rating generated yet.</p>}
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#fce7f3] p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-bold text-[#c084a0]">v{r.version}</span>
                <StatusPill label={r.status} tone={r.status === 'DRAFT' ? 'slate' : 'blue'} />
                <div>
                  <span className="text-[10px] font-bold uppercase text-[#c084a0]">AI drafted </span>
                  <span className="text-lg font-black text-[#6b0030]">{r.aiGrade}</span> <span className="text-xs text-[#9d0050]">{r.aiScore}/100</span>
                </div>
                {r.aiBand && <StatusPill label={BAND[r.aiBand].label} tone={BAND[r.aiBand].tone} />}
                <span className="text-[#e2b3c8]">→</span>
                <div>
                  <span className="text-[10px] font-bold uppercase text-[#c084a0]">Published </span>
                  <span className="text-lg font-black text-[#6b0030]">{r.grade}</span> <span className="text-xs text-[#9d0050]">{r.score}/100</span>
                </div>
                {r.accepted === true && <StatusPill label="AI accepted" tone="green" />}
                {r.accepted === false && <StatusPill label="Analyst changed" tone="amber" />}
                <span className="ml-auto text-xs text-[#c084a0]">
                  {r.promptFamily ? `${r.promptFamily} v${r.promptVersion}` : r.model}
                  {r.resolverReason ? ` · ${RESOLVER[r.resolverReason] || r.resolverReason}` : ''}
                </span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[#6b0030]">{r.summary}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Documents — AI verdict vs analyst" subtitle="Every uploaded version, including replaced ones. Cost is the verification call(s) for that file.">
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="border-b border-[#fce7f3]">
              <tr>
                <th className={TH}>Document</th>
                <th className={TH}>AI verdict</th>
                <th className={TH}>Analyst / current</th>
                <th className={TH}>Match</th>
                <th className={TH}>AI findings</th>
                <th className={`${TH} text-right`}>Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#fdf2f8]">
              {documents.map((d) => {
                const ai = VERDICT[d.aiState];
                const current = VERDICT[d.status];
                return (
                  <tr key={d.id} className={d.status === 'SUPERSEDED' ? 'opacity-60' : ''}>
                    <td className={TD}>
                      <p className="font-semibold text-[#6b0030]">{d.title}</p>
                      <p className="text-[11px] text-[#c084a0]">
                        {d.code} · v{d.version} · {d.fileName}
                      </p>
                    </td>
                    <td className={TD}>
                      <StatusPill label={ai?.label || d.aiState} tone={ai?.tone || 'slate'} />
                      {d.aiVerdict && d.aiConfidence != null && <p className="mt-1 text-[11px] text-[#b0708f]">{d.aiConfidence}% confident</p>}
                    </td>
                    <td className={TD}>
                      <StatusPill label={current?.label || d.status} tone={current?.tone || 'slate'} />
                      {d.reviewedBy && <p className="mt-1 text-[11px] text-[#b0708f]">by {d.reviewedBy.name || d.reviewedBy.email}</p>}
                    </td>
                    <td className={`${TD} text-base font-black`}>
                      {d.agreement === true && <span className="text-[#047857]">✓</span>}
                      {d.agreement === false && <span className="text-[#be123c]">✗</span>}
                      {d.agreement === null && <span className="text-xs font-normal text-[#c084a0]">—</span>}
                    </td>
                    <td className={`${TD} max-w-[420px] text-xs text-[#6b0030]`}>
                      {d.aiIssues?.length ? (
                        <ul className="list-disc pl-4 text-[#9d0050]">
                          {d.aiIssues.slice(0, 3).map((i, n) => (
                            <li key={n}>{i}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="line-clamp-2">{d.aiSummary || '—'}</p>
                      )}
                      {d.reviewNote && <p className="mt-1 italic text-[#b0708f]">Analyst: {d.reviewNote}</p>}
                    </td>
                    <td className={`${TD} text-right text-xs`}>
                      {formatUsd(d.ai.costUsd)}
                      <p className="text-[11px] text-[#c084a0]">
                        {formatTokens(d.ai.tokens)} tok · {(d.ai.latencyMs / 1000).toFixed(1)}s
                      </p>
                      {d.ai.failed > 0 && <p className="text-[11px] font-bold text-[#be123c]">{d.ai.failed} failed</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="AI call log" subtitle="Newest first. Demo document generation is listed for transparency but excluded from pipeline cost.">
        <div className="-mx-5 max-h-[520px] overflow-auto">
          <table className="w-full min-w-[900px] text-xs">
            <thead className="sticky top-0 border-b border-[#fce7f3] bg-white">
              <tr>
                <th className={TH}>When</th>
                <th className={TH}>Feature</th>
                <th className={TH}>Prompt</th>
                <th className={TH}>Document</th>
                <th className={`${TH} text-right`}>In / out tokens</th>
                <th className={`${TH} text-right`}>Latency</th>
                <th className={`${TH} text-right`}>Cost</th>
                <th className={TH}>Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#fdf2f8] text-[#6b0030]">
              {usage.map((u) => (
                <tr key={u.id} className={u.feature === 'DEMO_DOC_GENERATION' ? 'text-[#94a3b8]' : ''}>
                  <td className="px-3 py-1.5 whitespace-nowrap">{formatDateTime(u.createdAt)}</td>
                  <td className="px-3 py-1.5">{FEATURE[u.feature] || u.feature}</td>
                  <td className="px-3 py-1.5">
                    {u.promptLabel ? (
                      <Link to="/prompts" className="font-semibold text-[#d6006e] hover:underline">
                        {u.promptLabel}
                      </Link>
                    ) : (
                      '—'
                    )}
                    {u.resolverReason && <span className="mt-0.5 block text-[10px] uppercase tracking-wider text-[#c084a0]">{RESOLVER[u.resolverReason] || u.resolverReason}</span>}
                    {u.outputPreview && (
                      <pre className="mt-1 max-h-16 max-w-[280px] overflow-auto whitespace-pre-wrap font-mono text-[10px] leading-snug text-[#9d0050]">{u.outputPreview}</pre>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{u.documentTitle || '—'}</td>
                  <td className="px-3 py-1.5 text-right">
                    {formatTokens(u.promptTokens)} / {formatTokens(u.outputTokens)}
                  </td>
                  <td className="px-3 py-1.5 text-right">{(u.latencyMs / 1000).toFixed(1)}s</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{formatUsd(u.costUsd)}</td>
                  <td className="px-3 py-1.5">
                    {u.success ? <StatusPill label="OK" tone="green" /> : <span className="font-mono text-[11px] text-[#be123c]">{u.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
